import { useState } from 'react'
import { groupLabels } from '../principleDecisions/responsibleGroups'
import ResponsibleGroups from '../../components/ui/ResponsibleGroups'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import { deleteLabel } from '../../lib/deleteLabel'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import {
  hasDescription,
  responsibleLabels,
  type MinuteItem,
  type MinuteItemKind,
} from './types'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import PrincipleFilesDialog from '../principleDecisions/components/PrincipleFilesDialog'
import '../actions/actions.css'
import '../minutes/minutes.css'

interface Props {
  title: string
  kind: MinuteItemKind
  items: MinuteItem[]
  /**
   * Madde silindi (2026-09-11, kullanıcı isteği). **Verilmezse silme kolonu hiç çizilmez** — liste salt
   * okunur kalır (bugünkü davranış). Çağıran bunu yalnız `RECORD_DELETE` yetkisi varken geçer; onay
   * penceresi ve mutasyon da orada durur, bu bileşen sunum katmanı olarak kalır.
   */
  onDelete?: (item: MinuteItem) => void
}

/**
 * Toplantı Detay'daki Prensip Kararı / Karar / Gündem salt-okuma listesi (analiz 4.8–4.10). No · Metin
 * (+ Gündem'de Açıklama). {@link ActionsView} ile aynı desen; skor/termin/durum kolonları yoktur.
 */
export default function MinuteItemsView({ title, kind, items, onDelete }: Readonly<Props>) {
  const { t } = useTranslation()
  const withDescription = hasDescription(kind)
  // 077 — Kayıt No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde (Özel Kalem).
  const showRecordNumber = useGrants().hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  // Dar ekranda (≤CARD_VIEW_BREAKPOINT) tablo yerine kart listesi — bkz. MinuteItemViewCards.
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  /**
   * Dosyalar pop-up'ı (108) — bu görünüm **salt okunur** olduğu için `canManage` verilmez: yükleme ve
   * Kaldır çizilmez, görüntüleme/indirme kalır.
   *
   * <p><b>Neden burada da var:</b> bu görünüm arşivli toplantıda (ve düzenleme yetkisi olmayan
   * kullanıcıda) editörün YERİNE çiziliyor. Yalnız editöre konsaydı arşivlenen bir toplantının prensip
   * kararı dosyalarına Toplantı Detay'dan hiç erişilemezdi — oysa ekranın yerleşik kuralı "arşivde
   * dosya **yönetimi** kapalı, **indirme açık**" (bkz. `MeetingDetailPage.meetingPermissions`).
   */
  const canViewFiles = useGrants().hasGrant(GRANTS.PRINCIPLE_DECISION_VIEW)
  const showFiles = kind === 'principles' && canViewFiles
  const [filesId, setFilesId] = useState<number | null>(null)
  const filesItem = filesId == null ? null : (items.find((i) => i.id === filesId) ?? null)

  return (
    <section className="minutes-section">
      <div className="minutes-section-head">
        <h2>{title}</h2>
      </div>
      {items.length === 0 ? (
        <EmptyState
          icon="pi pi-list"
          title={t(`minuteItems.${kind}.emptyTitle`)}
          description={t(`minuteItems.${kind}.empty`)}
        />
      ) : isMobile ? (
        <div className="ui-card-list minuteitem-cards">
          {items.map((row) => {
            const removeLabel = deleteLabel(t, 'minuteItems.common.remove', row.name)
            return (
            <div key={row.id} className="ui-card">
              <div className="ui-card-head">
                <span className="ui-card-title">{row.name}</span>
                {/* Sil — masaüstü kolonunun kart karşılığı (2026-09-11). Verilmezse çizilmez.
                    Dosyalar düğmesi (108) aynı alanda; ikisi de yoksa alan hiç çizilmez. */}
                {(onDelete || showFiles) && (
                  <div className="ui-card-actions">
                    {showFiles && (
                      <button
                        type="button"
                        className="minutes-remove-btn"
                        aria-label={t('minuteItems.files.open', { name: row.name || '—' })}
                        title={t('minuteItems.files.open', { name: row.name || '—' })}
                        onClick={() => setFilesId(row.id)}
                      >
                        <i className="pi pi-paperclip" aria-hidden="true" />
                      </button>
                    )}
                    {onDelete && (
                    <button
                      type="button"
                      className="minutes-remove-btn"
                      aria-label={removeLabel}
                      title={removeLabel}
                      onClick={() => onDelete(row)}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                    </button>
                    )}
                  </div>
                )}
              </div>
              {showRecordNumber && row.recordNumber && (
                <span className="ui-card-sub">{row.recordNumber}</span>
              )}
              {withDescription && row.description && (
                <div className="ui-card-row act-card-desc">{row.description}</div>
              )}
              {(row.responsibles ?? []).length > 0 && (
                <div className="ui-card-row">
                  {groupLabels(row.responsibles ?? [])}
                </div>
              )}
            </div>
            )
          })}
        </div>
      ) : (
        <DataTable value={items} paginator={false}>
          {showRecordNumber && (
            <Column
              field="recordNumber"
              header={t(`minuteItems.${kind}.number`)}
              body={(row: MinuteItem) => row.recordNumber || '—'}
            />
          )}
          <Column
            header={t(`minuteItems.${kind}.name`)}
            body={(row: MinuteItem) => (
              <span className="minutes-cell-strong act-cell-clip act-cell-clip--wide" title={row.name}>
                {row.name}
              </span>
            )}
          />
          {withDescription && (
            <Column
              header={t(`minuteItems.${kind}.description`)}
              body={(row: MinuteItem) => (
                <span className="act-cell-clip act-cell-clip--wide" title={row.description ?? ''}>
                  {row.description ?? ''}
                </span>
              )}
            />
          )}
          <Column
            header={t(responsibleLabels(kind).label)}
            // `tk-cell-wrap`: global `td { white-space: nowrap }` bu hücrede kapanır — gruplanmış
            // sorumlu metni sarmalı, tabloyu taşırmamalı (push kapısı bulgusu 01.10.2026).
            bodyClassName="tk-cell-wrap"
            body={(row: MinuteItem) => (
              // Gösterim Prensip Kararları sayfasıyla ORTAK (kullanıcı isteği 01.10.2026): liste ile
              // eklenenler liste adı altında, üzerine gelinince kişiler.
              <ResponsibleGroups responsibles={row.responsibles} emptyText="—" />
            )}
          />
          {/* Dosyalar (108) — salt okunur: görüntüle/indir. Prensip Kararı dışındaki tiplerde ve
              PRINCIPLE_DECISION_VIEW yoksa kolon hiç çizilmez. */}
          {showFiles && (
            <Column
              header=""
              headerStyle={{ width: '4rem' }}
              bodyStyle={{ textAlign: 'center' }}
              body={(row: MinuteItem) => {
                const label = t('minuteItems.files.open', { name: row.name || '—' })
                return (
                  <button
                    type="button"
                    className="minutes-remove-btn"
                    aria-label={label}
                    title={label}
                    onClick={() => setFilesId(row.id)}
                  >
                    <i className="pi pi-paperclip" aria-hidden="true" />
                  </button>
                )
              }}
            />
          )}
          {/* Sil — yalnız `onDelete` verildiğinde (RECORD_DELETE). Düzenleme yetkisi olan kullanıcı bu
              görünümü hiç görmez; o maddeyi editörden kaldırır. Düğme editördekiyle aynı sınıfı ve
              ikonunu kullanır → iki yüzeyde aynı görünür. */}
          {onDelete && (
            <Column
              header=""
              headerStyle={{ width: '4rem' }}
              bodyStyle={{ textAlign: 'center' }}
              body={(row: MinuteItem) => {
                const removeLabel = deleteLabel(t, 'minuteItems.common.remove', row.name)
                return (
                  <button
                    type="button"
                    className="minutes-remove-btn"
                    aria-label={removeLabel}
                    title={removeLabel}
                    onClick={() => onDelete(row)}
                  >
                    <i className="pi pi-trash" aria-hidden="true" />
                  </button>
                )
              }}
            />
          )}
        </DataTable>
      )}

      {/* Salt okunur: `canManage` verilmez → yükleme ve Kaldır çizilmez, görüntüle/indir kalır. */}
      {showFiles && filesItem != null && (
        <PrincipleFilesDialog
          decision={{
            id: filesItem.id,
            recordNumber: filesItem.recordNumber,
            description: filesItem.name,
          }}
          canManage={false}
          onHide={() => setFilesId(null)}
        />
      )}
    </section>
  )
}
